import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { getApiUrl, API_URL } from '@/utils/api';
import { mapUser } from '@/utils/user';
import { AddressModel, UserModel } from '@/models';

import ApiBase from './ApiBase';

export default class UserService extends ApiBase {
  static baseUrl = getApiUrl(API_URL.USER, '/user');

  get(storeId) {
    return this.api.getJSON(`${UserService.baseUrl}?storeId=${storeId}`).pipe(
      map(mapUser)
    );
  }

  update(user: UserModel) {
    return this.api.putJSON(UserService.baseUrl, user).pipe(
      map(mapUser)
    );
  }

  updateAddress(address: Partial<AddressModel>) {
    return this.api.putJSON(`${UserService.baseUrl}/updateAddress`, address).pipe(
      map(mapUser)
    );
  }

  deleteAddress(addressId: string) {
    return this.api.deleteJSON(`${UserService.baseUrl}/deleteAddress`, { id: addressId }).pipe(
      map(mapUser)
    );
  }

  createAddresses(addresses: AddressModel[]) {
    return this.api.postJSON(`${UserService.baseUrl}/create-addresses`, addresses).pipe(
      map(mapUser)
    );
  }

  updatePassword(password: string, oldPassword?: string) {
    const payload: any = { password };
    let path = 'updatePassword'; // Used by reset password form but this route is deprecated

    if (oldPassword) {
      payload.oldPassword = oldPassword;
      path = 'update-password'; // Used by account page (change password form)
    }

    return this.api.putJSON(`${UserService.baseUrl}/${path}`, payload).pipe(
      map(mapUser)
    );
  }

  updateEmail(password: string, email: string) {
    return this.api.putJSON(`${UserService.baseUrl}/update-email`, { password, email }).pipe(
      map(mapUser)
    );
  }

  getCreditCards(paymentModes) {
    return this.api.putJSON(`${UserService.baseUrl}/getUserCard`, paymentModes);
  }

  addCreditCard(card) {
    return this.api.postJSON(`${UserService.baseUrl}/addUserCard`, card);
  }

  deleteCreditCard(cardId: string, isAdyen: boolean, paymentModes: any[]) {
    return this.api.deleteJSON(`${UserService.baseUrl}/deleteUserCard`, { id: cardId, isAdyen, paymentModes });
  }

  migrateCart(destinationUserId: string) {
    return this.api.putJSON(`${UserService.baseUrl}/migrate-cart`, destinationUserId);
  }

  getUserById(userId: string) {
    return this.api.getJSON(`${UserService.baseUrl}/users/${userId}`).pipe(
      map(mapUser)
    );
  }

  setUserLevel(storeId, userId, level, fromCsr, levelAmountProgress) {
    return this.api.postJSON(`${UserService.baseUrl}/changeLevel`, {
      storeId,
      userId,
      level,
      fromCsr,
      levelAmountProgress
    });
  }

  updateLoyaltyProgram() {
    return this.api.getJSON(`${UserService.baseUrl}/updateLoyaltyProgram`);
  }

  switchStore({ targetStoreId, sourceStoreId, storeIdForPrice, targetUrl, sourceUrl, checkoutStep = '' }) {
    const url = `switchStore?targetStoreId=${targetStoreId}&sourceStoreId=${sourceStoreId}&storeIdForPrice=${storeIdForPrice}&targetUrl=${targetUrl}&sourceUrl=${sourceUrl}&checkoutStep=${checkoutStep}`;
    const uri = encodeURI(url);
    return lastValueFrom(this.api.getJSON(
      `${UserService.baseUrl}/${uri}`
    ));
  }

  switchStoreUpdateCart({ targetStoreId, sourceStoreId, storeIdForPrice }) {
    const url = `switchStore/updateCart?targetStoreId=${targetStoreId}&sourceStoreId=${sourceStoreId}&storeIdForPrice=${storeIdForPrice}`;
    const uri = encodeURI(url);
    return lastValueFrom(this.api.getJSON(
      `${UserService.baseUrl}/${uri}`
    ));
  }

  updateUserCommunicationPreferences(storeId: string, langComm: string) {
    return this.api.putJSON(`${UserService.baseUrl}/updateCommunicationPreferences`, { storeId, langComm }).pipe(
      map(mapUser)
    );
  }
}
